import { useState } from 'react';
import { api, localTime, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Loading, Sheet, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

interface Location { id: string; name: string; timezone: string; region: string | null; screenCount: number }
interface Preview {
  timezone: string; from: string; to: string; locationName: string;
  screens: { screenId: string; screenName: string; segments: { startUtc: string; endUtc: string; programId: string | null; kind: string | null; name: string | null }[] }[];
}

const ZONES = ['America/Chicago', 'America/Phoenix', 'America/New_York', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Berlin', 'Asia/Tokyo', 'Australia/Sydney', 'UTC'];

export function Locations() {
  const org = useOrg();
  const { data, error, loading, reload } = useApi<{ locations: Location[] }>(orgPath(org, '/locations'), [org.id]);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<Location | null>(null);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Locations</h1>
          <p>Each location has its own time zone. “9 AM” schedules follow local time; UTC schedules run at the same instant everywhere.</p>
        </div>
        {can(org, 'locations.manage') && org.locationScope === null && <button className="btn btn-primary" onClick={() => setCreating(true)}><Icon name="plus" /> Add location</button>}
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading />}
      {data && !data.locations.length && <div className="card"><Empty title="No locations yet">Add a store, office or venue to start pairing screens there.</Empty></div>}
      {data && !!data.locations.length && (
        <div className="card table-wrap">
          <table className="table">
            <thead><tr><th>Location</th><th>Time zone</th><th>Local time now</th><th>Screens</th><th /></tr></thead>
            <tbody>
              {data.locations.map((l) => (
                <tr key={l.id}>
                  <td><strong>{l.name}</strong>{l.region && <div className="subtle">{l.region}</div>}</td>
                  <td className="mono">{l.timezone}</td>
                  <td>{localTime(new Date().toISOString(), l.timezone, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}</td>
                  <td>{l.screenCount}</td>
                  <td><button className="btn btn-sm" onClick={() => setPreview(l)}>Next 7 days</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <CreateLocation onClose={() => setCreating(false)} onCreated={reload} />}
      {preview && <SchedulePreview location={preview} onClose={() => setPreview(null)} />}
    </>
  );
}

function CreateLocation({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [region, setRegion] = useState('');
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    try {
      await api('POST', orgPath(org, '/locations'), { name, timezone, region: region || null });
      toast(`${name} added`);
      onCreated();
      onClose();
    } catch (e) {
      setErr((e as ApiFailure).message);
    }
  }
  return (
    <Sheet title="Add location" onClose={onClose} footer={<button className="btn btn-primary" disabled={!name} onClick={() => void save()}>Add location</button>}>
      <div className="stack">
        {err && <div className="alert danger" role="alert">{err}</div>}
        <div className="field"><label htmlFor="loc-name">Name</label><input id="loc-name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field">
          <label htmlFor="loc-tz">Time zone</label>
          <input id="loc-tz" className="input" list="tz-list" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          <datalist id="tz-list">{ZONES.map((z) => <option key={z} value={z} />)}</datalist>
          <span className="hint">IANA name, e.g. America/Chicago. Daylight-saving changes are handled automatically.</span>
        </div>
        <div className="field"><label htmlFor="loc-region">Region (optional)</label><input id="loc-region" className="input" value={region} onChange={(e) => setRegion(e.target.value)} /></div>
      </div>
    </Sheet>
  );
}

function SchedulePreview({ location, onClose }: { location: Location; onClose: () => void }) {
  const org = useOrg();
  const { data, error } = useApi<Preview>(orgPath(org, `/locations/${location.id}/schedule-preview?days=7`), [location.id]);
  const days = data ? Array.from({ length: 7 }, (_, i) => Date.parse(data.from) + i * 86_400_000) : [];
  return (
    <Sheet title={`${location.name} — next 7 days`} subtitle={`All times in ${location.timezone}. Players hold a signed 14-day schedule and fall back to house content if offline beyond it.`} onClose={onClose} wide>
      {error && <ErrorState error={error} />}
      {!data ? <Loading /> : !data.screens.length ? <Empty title="No screens at this location" /> : (
        <div className="stack" style={{ gap: 20 }}>
          <div className="legend">
            {[['playlist', '#3478f6'], ['campaign', '#8e44ef'], ['takeover', '#e5862b'], ['emergency', '#d7263d'], ['fallback', '#7d8590']].map(([k, c]) => <span key={k} style={{ ['--c' as string]: c }}>{k}</span>)}
          </div>
          {data.screens.map((s) => (
            <div key={s.screenId} className="stack">
              <h3>{s.screenName}</h3>
              {days.map((d) => {
                const dayStart = d;
                const dayEnd = d + 86_400_000;
                const segs = s.segments.filter((x) => Date.parse(x.endUtc) > dayStart && Date.parse(x.startUtc) < dayEnd);
                return (
                  <div key={d} className="row" style={{ flexWrap: 'nowrap' }}>
                    <span className="subtle" style={{ width: 120, flex: 'none' }}>{localTime(new Date(d).toISOString(), location.timezone, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric' })}</span>
                    <div className="timeline" style={{ flex: 1 }} role="img" aria-label={segs.map((x) => `${x.name ?? 'nothing'} from ${localTime(x.startUtc, location.timezone)} to ${localTime(x.endUtc, location.timezone)}`).join('; ')}>
                      {segs.map((x) => {
                        const a = Math.max(Date.parse(x.startUtc), dayStart);
                        const b = Math.min(Date.parse(x.endUtc), dayEnd);
                        return (
                          <div key={x.startUtc} className={`seg ${x.kind ?? 'none'}`} style={{ left: `${((a - dayStart) / 864e5) * 100}%`, width: `${((b - a) / 864e5) * 100}%` }} title={`${x.name ?? 'Nothing scheduled'} · ${localTime(x.startUtc, location.timezone)} – ${localTime(x.endUtc, location.timezone)}`}>
                            {x.name ?? ''}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}
